<script setup>
import {
    nextTick,
    ref,
} from 'vue';
import useVuelidate from '@vuelidate/core';
import {
    requiredIf,
    maxLength,
} from '@vuelidate/validators';
import { storeToRefs } from 'pinia';

import AppIcon from '../atoms/app/AppIcon.vue';
import FormElement from '../molecules/form/FormElement.vue';
import FormSection from './form/FormSection.vue';
import FormMessage from '../atoms/form/FormMessage.vue';
import { MAX_APPLICATION_LETTER_LENGTH } from '../../utils/constants/validationLimits';
import IconListItem from './IconListItem.vue';
import { useApplicationStore } from '../../stores/application-store';
import LimitedTextarea from '../atoms/form/LimitedTextarea.vue';
import { useBreakpoints } from '../../composables/breakpoint';

const application = useApplicationStore();
const { applicationLetter } = storeToRefs(application);
const { isMobile } = useBreakpoints();

const limitedTextarea = ref(null);

const validations = {
    applicationLetter: {
        required: requiredIf(() => application.showApplicationLetter),
        maxLength: maxLength(MAX_APPLICATION_LETTER_LENGTH),
    },
};

const v$ = useVuelidate(validations, { applicationLetter }, { $registerAs: 'applicationLetter' });

async function addApplicationLetter() {
    application.addApplicationLetter();
    await nextTick();
    limitedTextarea.value.focusTextarea();
    v$.value.applicationLetter.$reset();
}

defineExpose({ v$ });
</script>

<template>
    <FormSection
        data-qa="form section"
        class="o-applicationLetter"
    >
        <FormElement>
            <IconListItem
                v-if="!application.showApplicationLetter"
                class="o-applicationLetter__actionItem"
                tabindex="0"
                data-gtm-element="AP_F: Remarks"
                data-gtm-element-detail="Added"
                data-qa="application letter empty state"
                underline-title
                @click="addApplicationLetter"
                @keyup.enter.space="addApplicationLetter"
            >
                <template
                    v-if="!isMobile"
                    #icon
                >
                    <div class="o-applicationLetter__iconContainer">
                        <AppIcon
                            name="plus"
                            size="xl"
                        />
                    </div>
                </template>
                <template #title>
                    Anmerkung hinzufügen
                </template>
                <template #subtitle>
                    Willst du der Recruiter*in noch etwas mitteilen?
                </template>
            </IconListItem>
            <LimitedTextarea
                v-else
                ref="limitedTextarea"
                v-model="applicationLetter"
                data-qa="application letter"
                placeholder="Deine Anmerkungen zu Motivation, Verfügbarkeit, Gehalt und mehr ..."
                :status="v$.applicationLetter.$error ? 'error' : ''"
                :limit="MAX_APPLICATION_LETTER_LENGTH"
                :trim="false"
                @update:model-value="v$.$touch()"
                @blur="v$.$touch()"
            />
            <template #end>
                <FormMessage
                    v-if="v$.applicationLetter.$error && application.showApplicationLetter"
                    type="error"
                    data-qa="application letter message"
                >
                    <div v-if="v$.applicationLetter.maxLength.$invalid">
                        Du kannst nur {{ MAX_APPLICATION_LETTER_LENGTH }} Zeichen eingeben.
                    </div>
                    <template v-if="v$.applicationLetter.required.$invalid">
                        Bitte gib eine Nachricht ein.
                    </template>
                </FormMessage>
            </template>
        </FormElement>
    </FormSection>
</template>

<style lang="scss">
.o-applicationLetter {
    &__iconContainer {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
        height: 100%;
        inset: 0;
        background-color: var(--k-white);
    }

    &__actionItem {
        &:hover {
            cursor: pointer;
        }
    }
}
</style>
